import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from "@/api/base44Client";
import { 
  Plus, 
  Bot, 
  User,
  Sparkles,
  Loader2,
  AlertTriangle,
  Check,
  ChevronsUpDown,
  CalendarIcon
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { format, parseISO } from 'date-fns';
import TaskCategorizationPanel from '@/components/gamification/TaskCategorizationPanel';

export default function TaskQuickAdd({ 
  activity, 
  officers, 
  currentUserEmail,
  onClose,
  existingTaskCount,
  editingTask
}) {
  const queryClient = useQueryClient();
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiAssessment, setAiAssessment] = useState(null);
  
  const isEditing = !!editingTask;
  
  const [form, setForm] = useState({
    task_description: editingTask?.task_description || '',
    key_result_focus: editingTask?.key_result_focus || '',
    assign_to_email: editingTask?.user_email || activity.responsible_email,
    date: editingTask?.date || format(new Date(), 'yyyy-MM-dd'),
    task_order: editingTask?.task_order || existingTaskCount + 1,
    time_allocation_percent: editingTask?.time_allocation_percent || (existingTaskCount === 0 ? 40 : existingTaskCount === 1 ? 30 : 20),
    // Gamification fields
    category: editingTask?.category || null,
    xp_value: editingTask?.xp_value || 0,
    ep_value: editingTask?.ep_value || 0,
    skill_rewards: editingTask?.skill_rewards || [],
    bot_suggested_category: editingTask?.bot_suggested_category || null,
    bot_suggested_xp: editingTask?.bot_suggested_xp || null,
    bot_suggested_ep: editingTask?.bot_suggested_ep || null,
    bot_suggested_skills: editingTask?.bot_suggested_skills || null,
    bot_suggestion_approved: editingTask?.bot_suggestion_approved || false
  });

  const [assignPopoverOpen, setAssignPopoverOpen] = useState(false);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  
  const targetOfficer = officers.find(o => o.user_email === form.assign_to_email);
  const isAIOfficer = targetOfficer?.is_bot || targetOfficer?.custom_role === 'AI Officer';
  const isAssigningToSelf = form.assign_to_email === currentUserEmail;
  const isTentative = !isAssigningToSelf && !isAIOfficer;

  const createTaskMutation = useMutation({
    mutationFn: async (data) => {
      return base44.entities.DailyTask.create(data);
    },
    onSuccess: async (createdTask) => {
      queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
      
      // Send notification for tentative tasks
      if (isTentative) {
        try {
          await base44.functions.invoke('sendNotification', {
            recipient_email: form.assign_to_email,
            type: 'task_tentative',
            source_type: 'daily_task',
            source_id: createdTask.id,
            source_code: activity?.activity_code,
            source_name: form.task_description,
            triggered_by_email: currentUserEmail,
            deep_link_params: {
              date: form.date,
              user_email: form.assign_to_email,
              task_id: createdTask.id
            },
            metadata: {
              task_description: form.task_description,
              activity_code: activity?.activity_code
            }
          });
        } catch (err) {
          console.error('Failed to send notification:', err);
        }
      }
      
      onClose?.();
    }
  });

  const updateTaskMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      return base44.entities.DailyTask.update(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
      onClose?.();
    }
  });

  const handleSubmit = () => {
    if (!form.task_description.trim()) {
      alert('Task description is required');
      return;
    }

    const taskData = {
      activity_id: activity.id,
      task_description: form.task_description,
      key_result_focus: form.key_result_focus,
      user_email: form.assign_to_email,
      date: form.date,
      task_order: form.task_order,
      time_allocation_percent: isAIOfficer ? 0 : form.time_allocation_percent,
      // Gamification fields
      category: form.category,
      xp_value: form.xp_value || 0,
      ep_value: form.ep_value || 0,
      skill_rewards: form.skill_rewards || [],
      bot_suggested_category: form.bot_suggested_category,
      bot_suggested_xp: form.bot_suggested_xp,
      bot_suggested_ep: form.bot_suggested_ep,
      bot_suggested_skills: form.bot_suggested_skills,
      bot_suggestion_approved: form.bot_suggestion_approved || false
    };

    if (isEditing) {
      updateTaskMutation.mutate({ id: editingTask.id, data: taskData });
    } else {
      taskData.status_flag = 'gray';
      taskData.status = isTentative ? 'tentative' : 'approved';
      taskData.generated_by_ai = false;
      taskData.ai_assessment = aiAssessment;
      
      if (isTentative) {
        taskData.created_by_email = currentUserEmail;
      }

      createTaskMutation.mutate(taskData);
    }
  };

  const handleAIGenerate = async () => {
    setIsGenerating(true);
    setAiAssessment(null);
    try {
      const res = await base44.functions.invoke('generateTasks', { 
        activity_id: activity.id,
        mode: 'generate',
        target: 'human'
      });
      if (res.data?.suggested_task) {
        setForm(prev => ({ ...prev, task_description: res.data.suggested_task }));
        setAiAssessment(res.data.assessment);
      }
    } catch (err) {
      console.error("Failed to generate task", err);
      alert("Failed to generate task. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Assignment Info */}
      {isTentative && (
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <div className="flex items-center gap-2 text-sm text-amber-300">
            <AlertTriangle className="w-4 h-4" />
            <span>
              This will be a tentative task requiring acceptance from {targetOfficer?.callsign || form.assign_to_email}
            </span>
          </div>
        </div>
      )}

      {isAIOfficer && (
        <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
          <div className="flex items-center gap-2 text-sm text-purple-300">
            <Bot className="w-4 h-4" />
            <span>Creating task for AI Officer: {targetOfficer?.callsign}</span>
          </div>
        </div>
      )}

      {/* Assign To */}
      <div>
        <label className="text-xs text-slate-400 mb-1.5 block">Assign To</label>
        <Popover open={assignPopoverOpen} onOpenChange={setAssignPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={assignPopoverOpen}
              className="w-full justify-between bg-slate-950 border-slate-700 text-slate-100 hover:bg-slate-900 hover:text-slate-100"
            >
              {targetOfficer ? (
                <div className="flex items-center gap-2">
                  {(targetOfficer.is_bot || targetOfficer.custom_role === 'AI Officer') && <Bot className="w-3 h-3 text-purple-400" />}
                  <span>{targetOfficer.callsign}</span>
                  <span className="text-slate-400">({targetOfficer.rank_title})</span>
                </div>
              ) : (
                "Select officer..."
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0 bg-white border-slate-300" align="start">
            <Command className="bg-white">
              <CommandInput placeholder="Search officers..." className="border-none text-slate-900" />
              <CommandList>
                <CommandEmpty className="text-slate-500 py-4 text-center text-sm">No officer found.</CommandEmpty>
                <CommandGroup className="p-1">
                  {officers.filter(officer => {
                    if (!officer.is_bot) return true;
                    return officer.bot_owner_email === currentUserEmail;
                  }).map(officer => {
                    const isBot = officer.is_bot || officer.custom_role === 'AI Officer';
                    return (
                      <CommandItem
                        key={officer.user_email}
                        value={`${officer.callsign} ${officer.rank_title} ${officer.user_email}`}
                        onSelect={() => {
                          setForm({ ...form, assign_to_email: officer.user_email });
                          setAssignPopoverOpen(false);
                        }}
                        className="cursor-pointer text-slate-900 hover:bg-slate-100 aria-selected:bg-slate-100"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4 text-indigo-600",
                            form.assign_to_email === officer.user_email ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex items-center gap-2 flex-1">
                          {isBot && <Bot className="w-3.5 h-3.5 text-purple-600" />}
                          <span className="font-medium">{officer.callsign}</span>
                          <span className="text-slate-500">({officer.rank_title})</span>
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Date */}
      <div>
        <label className="text-xs text-slate-400 mb-1.5 block">Date</label>
        <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal bg-slate-950 border-slate-700 text-slate-100 hover:bg-slate-900 hover:text-slate-100",
                !form.date && "text-slate-500"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {form.date ? format(parseISO(form.date), 'PPP') : "Pick a date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-white border-slate-300" align="start">
            <Calendar
              mode="single"
              selected={form.date ? parseISO(form.date) : undefined}
              onSelect={(date) => {
                if (date) {
                  setForm({ ...form, date: format(date, 'yyyy-MM-dd') });
                }
                setDatePopoverOpen(false);
              }}
              initialFocus
              className="bg-white text-slate-900"
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Key Result Focus */}
      {activity.key_results && activity.key_results.length > 0 && (
        <div>
          <label className="text-xs text-slate-400 mb-1.5 block">Key Result Focus</label>
          <Select 
            value={form.key_result_focus} 
            onValueChange={(v) => setForm({ ...form, key_result_focus: v })}
          >
            <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-100">
              <SelectValue placeholder="Select key result..." />
            </SelectTrigger>
            <SelectContent className="bg-slate-950 border-slate-800">
              {activity.key_results.filter(kr => kr.description).map((kr, idx) => (
                <SelectItem key={idx} value={kr.description}>
                  {kr.description} ({kr.current_value || 0}/{kr.target})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Task Description */}
      <div>
        <label className="text-xs text-slate-400 mb-1.5 block">Task Description</label>
        <Textarea data-testid="civ-taskquickadd-textarea"
          value={form.task_description}
          onChange={(e) => setForm({ ...form, task_description: e.target.value })}
          placeholder="What needs to be accomplished?"
          className="bg-slate-950 border-slate-700 text-slate-100 min-h-[80px]"
        />
        
        <Button 
          type="button"
          onClick={handleAIGenerate}
          disabled={isGenerating}
          className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          {isGenerating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              AI Generate Task
            </>
          )}
        </Button>

        {aiAssessment && aiAssessment.type !== 'ok' && (
          <div className={cn(
            "mt-2 text-sm p-3 rounded-md flex items-start gap-2",
            aiAssessment.type === 'redundant' 
              ? 'bg-red-500/10 border border-red-500/30 text-red-200' 
              : 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-200'
          )}>
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{aiAssessment.message}</span>
          </div>
        )}
      </div>

      {/* Task Order & Time Allocation (only for non-AI officers) */}
      {!isAIOfficer && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Task Order</label>
            <Select 
              value={form.task_order.toString()} 
              onValueChange={(v) => setForm({ ...form, task_order: parseInt(v) })}
            >
              <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-100">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-800">
                {[1,2,3,4,5].map(n => (
                  <SelectItem key={n} value={n.toString()}>Task #{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Time %</label>
            <Input data-testid="civ-taskquickadd-input"
              type="number"
              value={form.time_allocation_percent}
              onChange={(e) => setForm({ ...form, time_allocation_percent: parseInt(e.target.value) || 0 })}
              className="bg-slate-950 border-slate-700 text-slate-100"
              min={0}
              max={100}
            />
          </div>
        </div>
      )}

      {/* Gamification Points Panel */}
      <TaskCategorizationPanel
        taskDescription={form.task_description}
        keyResultFocus={form.key_result_focus}
        activityId={activity.id}
        onCategorize={(gamificationData) => {
          setForm(prev => ({
            ...prev,
            ...gamificationData
          }));
        }}
        initialValues={form.category ? {
          category: form.category,
          xp_value: form.xp_value,
          ep_value: form.ep_value,
          skill_rewards: form.skill_rewards
        } : null}
      />

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <Button 
          variant="outline" 
          onClick={onClose}
          className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          disabled={createTaskMutation.isPending || updateTaskMutation.isPending}
          className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          {(createTaskMutation.isPending || updateTaskMutation.isPending) ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {isEditing ? 'Saving...' : 'Creating...'}
            </>
          ) : (
            <>
              <Plus className="w-4 h-4 mr-2" />
              {isEditing ? 'Save Changes' : (isTentative ? 'Create Tentative' : 'Create Task')}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}